import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { describeError } from "@/api/client";
import { AlertDialog, Button, ButtonLink, CopyButton, InlineAlert, Skeleton, TextField, useToast } from "@/components";
import { calendarUrlQuery, useRotateCalendarToken } from "../api";
import styles from "../settings.module.css";

/** The private iCal feed of open reminders, with copy, subscribe and rotate. */
export function CalendarSection({ canEdit }: { canEdit: boolean }) {
  const calendar = useQuery(calendarUrlQuery);
  const rotate = useRotateCalendarToken();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const toast = useToast();

  return (
    <div className={styles.panel}>
      <div className={styles.stack}>
        <p className={styles.hint}>
          Subscribe to this address in your phone or desktop calendar to see re-smell, shelf-life, retest and reorder
          reminders. Anyone with the link can read the reminders, so keep it private.
        </p>
        {calendar.isPending ? (
          <Skeleton shape="control" />
        ) : calendar.isError ? (
          <InlineAlert
            tone="danger"
            title="Couldn’t load the calendar link"
            action={<Button onClick={() => void calendar.refetch()}>Retry</Button>}
          >
            {describeError(calendar.error)}
          </InlineAlert>
        ) : (
          <>
            <div className={styles.feedUrl}>
              <TextField
                label="Calendar feed address"
                value={calendar.data.url}
                readOnly
                mono
                onFocus={(event) => event.currentTarget.select()}
              />
              <CopyButton value={calendar.data.url} label="Copy link" />
            </div>
            <div className={styles.actionsRow}>
              <ButtonLink to={calendar.data.webcal_url} reloadDocument icon="calendar">
                Subscribe in calendar app
              </ButtonLink>
              {canEdit ? (
                <Button icon="refresh" variant="ghost" onClick={() => setConfirmOpen(true)}>
                  Rotate link…
                </Button>
              ) : null}
            </div>
          </>
        )}
      </div>
      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Rotate the calendar link?"
        description="The current link stops working. Calendars subscribed to it stop updating until you subscribe to the new link."
        confirmLabel="Rotate link"
        pending={rotate.isPending}
        onConfirm={() =>
          rotate.mutate(undefined, {
            onSuccess: () => {
              setConfirmOpen(false);
              toast.success("Calendar link rotated", {
                description: "Subscribe to the new link in your calendar apps.",
              });
            },
            onError: (error) => toast.error("Couldn’t rotate the calendar link", { description: describeError(error) }),
          })
        }
      />
    </div>
  );
}
